/**
 * 岗位匹配（猎头端候选人详情 · 仅株式会社 Linc 可见）
 *
 * - 列表、数量、各流转操作已对接 /ats-interview/api/v1/candidate/{candidateId}/job-matches/**
 * - 投递 / 添加沟通职位弹窗的职位状态直接用职位接口返回的 matchStatus；匹配度弹窗没有专门接口，用现有接口组合实现
 */
import { request } from './request'
import { checkDuplicateApplicationApi, type CounselingBooking } from './interview'
import { getCandidateSummaryApi, getHeadhuntingEnterpriseListApi, getHeadhuntingJobCandidateJobsApi } from './candidate'
import type { SummaryData } from '@/components/business-components/dialog/ai-score-dialog-types'

const baseUrl = (candidateId: number | string) => `/ats-interview/api/v1/candidate/${candidateId}/job-matches`

// ======================= 枚举 =======================

/** 求人在岗位匹配里的状态（岗位匹配列表的 status、职位接口的 matchStatus 共用） */
export enum JobMatchStatus {
  RECOMMEND = 'RECOMMENDED',
  /** 未沟通（职位接口 matchStatus） */
  NOT_COMMUNICATED = 'NOT_COMMUNICATED',
  COMMUNICATING = 'COMMUNICATING',
  PENDING_APPLY = 'PENDING_APPLICATION',
  APPLIED = 'APPLIED',
  NOT_CONSIDER = 'NOT_CONSIDERED',
}
/** 不考虑原因（接口 notConsideredReason / reason 字段） */
export enum NotConsiderReason {
  NOT_INTERESTED = 'NOT_INTERESTED',
  NOT_MATCH = 'NOT_MATCH',
  OTHER = 'OTHER',
}

// ======================= 类型 =======================

/** 岗位匹配列表项（字段与接口返回一致，不做转换） */
export interface JobMatchItem {
  jobId: number
  jobTitle: string
  enterpriseId: number
  enterpriseName: string
  /** 匹配度 */
  matchScore: number
  /** 匹配理由，匹配度弹窗里展示 */
  matchReason?: string
  status: JobMatchStatus
  memo?: string
  /** 不考虑原因 */
  notConsideredReason?: NotConsiderReason | null
  /** 不考虑的补充说明 */
  supplement?: string
  jobType?: number
  /** 职种 */
  jobTypeName?: string
  positionType?: number
  /** 職種・ポジション */
  positionName?: string
  /** web 掲載是否打开 */
  webPublished?: boolean
  /** 公开页链接，复制用 */
  webUrl?: string
  sourceBookingId?: number
  updatedAt?: string
  interviewId?: number
  interviewDetailUrl?: string
  /** 在留资格 */
  residenceTypeName?: string
  /** 日语等级 */
  japaneseLevel?: string
  /** 勤务地 */
  workLocation?: string
  /** 薪资 */
  salary?: string
  /** 行业 */
  jobIndustryName?: string
  /** 业种 */
  industryTypeName?: string
  updatedBy?: number
  /** 更新人 */
  updatedByName?: string
  /** 所属（勤務先） */
  affiliationName?: string
}

/** 各状态数量（key 为岗位匹配状态） */
export type JobMatchCounts = Partial<Record<JobMatchStatus, number>>

/** 投递 / 添加沟通职位弹窗的职位选项（字段与职位接口一致） */
export interface MatchApplyJobOption {
  jobId: number
  title: string
  jobType?: number
  affiliationId?: number
  affiliationName?: string
  positionType?: number
  positionTypeName?: string
  createTime?: string
  /** 岗位匹配状态（仅猎头端） */
  matchStatus?: JobMatchStatus
}

export interface EnterpriseOption {
  id: number
  name: string
}

interface CandidateJobPayload {
  candidateId: number | string
  jobId: number
}

/** 流转类接口共用的请求体 */
interface JobMatchActionBody {
  memo?: string
  reason?: string
  supplement?: string
  resumeType?: number
}

// ======================= 转换 =======================

/**
 * 数量接口返回结构未定，兼容两种常见写法，统一转成 { 状态: 数量 }：
 * - 对象：{ RECOMMENDED: 20, COMMUNICATING: 3, ... }
 * - 数组：[{ status: 'RECOMMENDED', count: 20 }, ...]
 */
const toJobMatchCounts = (raw: unknown): JobMatchCounts => {
  const entries: [string, unknown][] = Array.isArray(raw)
    ? raw.map((item: { status?: string; count?: number }) => [String(item.status), item.count])
    : Object.entries((raw as Record<string, unknown>) || {})
  return Object.fromEntries(entries.map(([status, count]) => [status, Number(count) || 0]))
}

// ======================= 列表 / 数量 =======================

/** 岗位匹配列表；不传 status 时返回全部状态 */
export function getJobMatchListApi(candidateId: number | string, status?: JobMatchStatus): Promise<JobMatchItem[]> {
  return request({ url: baseUrl(candidateId), method: 'GET', params: { status } })
}

/** 各状态数量 */
export async function getJobMatchCountsApi(candidateId: number | string): Promise<JobMatchCounts> {
  return toJobMatchCounts(await request({ url: `${baseUrl(candidateId)}/counts`, method: 'GET' }))
}

// ======================= 刷新匹配度 =======================

/** 匹配度刷新进度（刷新接口和进度接口返回相同结构） */
export interface JobMatchRefreshStatus {
  status: string
  /** 需要刷新的职位数 / 已完成数 / 失败数 */
  total: number
  finished: number
  failed: number
  startedAt?: string | null
  /** 最近一次刷新完成的时间，「更新于」展示用 */
  finishedAt?: string | null
}

/** 刷新岗位匹配度（后台异步执行，进度用 getJobMatchRefreshStatusApi 查询） */
export function refreshJobMatchApi(candidateId: number | string): Promise<JobMatchRefreshStatus> {
  return request({ url: `${baseUrl(candidateId)}/refresh`, method: 'POST' })
}

/** 岗位匹配度刷新进度 */
export function getJobMatchRefreshStatusApi(candidateId: number | string): Promise<JobMatchRefreshStatus> {
  return request({ url: `${baseUrl(candidateId)}/refresh-status`, method: 'GET' })
}

/**
 * 刷新是否进行中：已开始且还没有完成时间
 * TODO: status 取值未知，确认后可改为按 status 判断
 */
export const isJobMatchRefreshing = (data?: JobMatchRefreshStatus | null) => !!data?.startedAt && !data.finishedAt

// ======================= 流转操作 =======================

const postAction = (data: CandidateJobPayload, action: string, body?: JobMatchActionBody, method: 'POST' | 'PUT' = 'POST'): Promise<void> =>
  request({ url: `${baseUrl(data.candidateId)}/${data.jobId}/${action}`, method, data: body })

/** 标记感兴趣 → 沟通中 */
export function markJobInterestedApi(data: CandidateJobPayload & { memo?: string }): Promise<void> {
  return postAction(data, 'interest', { memo: data.memo })
}

/** 暂不考虑（新建或编辑原因/补充说明）→ 暂不考虑 */
export function notConsiderJobApi(data: CandidateJobPayload & { reason: NotConsiderReason; supplement?: string }): Promise<void> {
  return postAction(data, 'not-considered', { reason: data.reason, supplement: data.supplement })
}

/** 同意投递 → 待投递 */
export function agreeApplyJobApi(data: CandidateJobPayload): Promise<void> {
  return postAction(data, 'agree-apply')
}

/** 重新沟通 → 沟通中 */
export function reCommunicateJobApi(data: CandidateJobPayload): Promise<void> {
  return postAction(data, 'recommunicate')
}

/** 修改沟通备注 */
export function updateJobMatchMemoApi(data: CandidateJobPayload & { memo: string }): Promise<void> {
  return postAction(data, 'memo', { memo: data.memo }, 'PUT')
}

// ======================= 添加沟通职位 =======================

/** 不能添加到沟通中的状态：已投递、沟通中、待投递 */
const NOT_ADDABLE_STATUSES = [JobMatchStatus.APPLIED, JobMatchStatus.COMMUNICATING, JobMatchStatus.PENDING_APPLY]

/**
 * 添加沟通职位弹窗：某企业下可添加的求人 = 未投递过、且不在沟通中 / 待投递列表的
 * （职位接口只返回该候选人未投递过的职位，这里再按 matchStatus 兜底过滤）
 * TODO: 需求要求企业下拉只列出有可添加求人的企业，需后端支持；目前列出全部企业
 */
export async function getAddableCommunicatingJobsApi(candidateId: number | string, enterpriseId: number): Promise<MatchApplyJobOption[]> {
  const jobs: MatchApplyJobOption[] = (await getHeadhuntingJobCandidateJobsApi(candidateId, enterpriseId)) || []
  return jobs.filter(job => !job.matchStatus || !NOT_ADDABLE_STATUSES.includes(job.matchStatus))
}

/** 添加沟通职位：没有批量接口，逐个「标记感兴趣」 */
export async function addCommunicatingJobsApi(data: { candidateId: number | string; jobIds: number[] }): Promise<void> {
  await Promise.all(data.jobIds.map(jobId => markJobInterestedApi({ candidateId: data.candidateId, jobId })))
}

// ======================= 匹配度弹窗 =======================

/** 匹配度弹窗数据（复用 AI 评分弹窗）：候选人信息取摘要接口，分数和评语取列表行 */
export async function getJobMatchScoreDetailApi(candidateId: number | string, row: Pick<JobMatchItem, 'matchScore' | 'matchReason'>): Promise<SummaryData> {
  const summary: SummaryData = (await getCandidateSummaryApi(candidateId)) || { name: '' }
  return { ...summary, score: row.matchScore, content: row.matchReason || '' }
}

// ======================= 投递弹窗（Linc Agent 下使用） =======================

/** 投递弹窗：应募企业下拉 */
export function getMatchApplyEnterpriseOptionsApi(): Promise<EnterpriseOption[]> {
  return getHeadhuntingEnterpriseListApi()
}

/** 投递前重复校验 */
export function checkMatchDuplicateApplicationApi(data: CandidateJobPayload): Promise<{ isDuplicate: boolean; duplicateMessage?: string }> {
  return checkDuplicateApplicationApi({ candidateId: Number(data.candidateId), jobId: data.jobId })
}

/** 投递（岗位匹配） */
export function applyMatchJobApi(data: CandidateJobPayload & { reason: string }): Promise<void> {
  // TODO: resumeType 取值待后端确认，暂不传
  return postAction(data, 'apply', { reason: data.reason })
}

// ======================= AI 生成推荐理由 =======================

/** AI 生成推荐理由：可选的面谈（后端只返回面谈纪要不为空的） */
export function getRecommendReasonInterviewsApi(candidateId: number | string, jobId: number): Promise<CounselingBooking[]> {
  return request({ url: `${baseUrl(candidateId)}/${jobId}/recommendation-reason/interviews`, method: 'GET' })
}

/** AI 生成推荐理由（基于所选面谈的纪要），AI 生成耗时较长，不设超时 */
export function generateRecommendReasonApi(
  data: CandidateJobPayload & { bookingId: number },
): Promise<{ bookingId: number; reason: string }> {
  return request({
    url: `${baseUrl(data.candidateId)}/${data.jobId}/recommendation-reason/generate`,
    method: 'POST',
    data: { bookingId: data.bookingId },
    timeout: 0,
  })
}
